import type { FocusEvent, ReactNode } from 'react'
import { Children, useState } from 'react'

import type { Theme } from '@emotion/react'
import type { RegisterOptions, UseFormReturn } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'

import type { GetNativeInputElementProps } from '@twisto/components/atoms/input-base'
import { ChevronDownIcon } from '@twisto/icons'
import type { CssClasses } from '@twisto/styles'
import { normalizeReactNode } from '@twisto/utils'

import * as styles from './native-select.styles'

type CssClassesApi = Pick<
  typeof styles,
  'root' | 'select' | 'chevron' | 'startAdornment'
>

type Props = {
  startAdornment?: ReactNode
  children: ReactNode | ReactNode[]
  error?: boolean
  focused?: boolean
  name: string
  rules?: RegisterOptions
} & GetNativeInputElementProps<HTMLSelectElement> &
  CssClasses<CssClassesApi>

export const NativeSelect = ({
  styles: stylesProp = {},
  className,
  startAdornment,
  children,
  name,
  value,
  error: errorProp = false,
  focused: focusedProp = false,
  onBlur,
  onFocus,
  rules = {},
  ...props
}: Props) => {
  const [isFocused, setFocused] = useState(focusedProp)
  // cast to ensure missing provider is handled

  const { register, formState } =
    (useFormContext() as UseFormReturn | null) || {}
  const fieldErrors = formState?.errors ?? {}
  const hasError = errorProp || Boolean(fieldErrors[name])

  const handleBlur = (event: FocusEvent<HTMLSelectElement>) => {
    setFocused(false)
    if (onBlur) {
      onBlur(event)
    }
  }

  const handleFocus = (event: FocusEvent<HTMLSelectElement>) => {
    setFocused(true)
    if (onFocus) {
      onFocus(event)
    }
  }

  const hasMultipleChildren = Children.count(children) > 1

  const rootStyles = (theme: Theme) => [
    styles.root,
    isFocused && styles.focused(theme),
    hasError && styles.error(theme),
    stylesProp.root,
  ]

  const startAdornmentStyles = [
    styles.startAdornment,
    stylesProp.startAdornment,
  ]

  const selectStyles = [styles.select, stylesProp.select]
  const chevronStyles = [styles.chevron, stylesProp.chevron]

  const formProps = register?.(name, {
    onBlur: handleBlur,
    value,
    ...rules,
    ...props,
  })

  return (
    <div className={className} css={rootStyles}>
      {normalizeReactNode(startAdornment, {
        css: startAdornmentStyles,
        size: 'extraSmall',
      })}
      <select
        css={selectStyles}
        data-testid="native-select"
        name={name}
        value={value}
        onBlur={handleBlur}
        onFocus={handleFocus}
        {...props}
        {...formProps}
      >
        {children}
      </select>
      {hasMultipleChildren && <ChevronDownIcon css={chevronStyles} size="18" />}
    </div>
  )
}
